/**
 * Testi italiani della procedura «Nuovo set» in quattro passi, con l'anteprima
 * dal vivo (ADR 0030 §3, tappa 4).
 *
 * I nomi dei passi sono brevi (Ant Design: meno di 12 caratteri per `Steps`);
 * i titoli dei passi sono sostantivi, come ogni titolo.
 */

/** Testi della procedura. */
export const setup = {
  /** Nomi dei passi nella barra. */
  steps: {
    /** Nome accessibile della barra dei passi. */
    label: 'Passi della procedura',
    /** Primo passo. */
    name: 'Nome',
    /** Secondo passo. */
    resources: 'Risorse',
    /** Terzo passo. */
    credential: 'Credenziale',
    /** Quarto passo. */
    project: 'Progetto',
  },
  /** Titoli dei passi. */
  titles: {
    /** Primo passo. */
    name: 'Nome del set',
    /** Secondo passo. */
    resources: 'Risorse del set',
    /** Terzo passo. */
    credential: 'Credenziale di Claude',
    /** Quarto passo. */
    project: 'Crea il tuo primo progetto',
  },
  /** Comandi comuni ai passi. */
  nav: {
    /** Passo seguente. */
    next: 'Avanti',
    /** Passo precedente. */
    back: 'Indietro',
  },
  /** Passo del nome. */
  name: {
    /** Che cosa nasce. */
    intro: 'Il set nasce con un agente Institore: è a lui che scriverai, e lui organizza gli altri agenti.',
    /** Etichetta del nome. */
    name: 'Nome del set',
    /** Etichetta dello slug. */
    slug: 'Slug',
    /** Vincoli dello slug. */
    slugHelp: 'Da 3 a 64 caratteri: minuscole, cifre e trattini. Non si può cambiare dopo la creazione.',
    /** Etichetta della descrizione. */
    description: 'Descrizione',
    /** Nome mancante. */
    nameMissing: 'Scrivi il nome del set.',
    /** Slug già usato. */
    slugTaken: 'Lo slug è già usato da un altro set. Scegline un altro.',
  },
  /** Passo delle risorse: qui il set nasce. */
  resources: {
    /** Che cosa si sceglie. */
    intro: 'Taglia e profilo si possono cambiare dopo, dalle impostazioni del set. Con «Crea set» il set nasce, con l’Institore fermo.',
    /** Etichetta della taglia. */
    size: 'Taglia',
    /** Aiuto della taglia. */
    sizeHelp: 'Decide CPU e memoria del set. Il piano può limitare la taglia più grande.',
    /** Spiegazione di ogni taglia. */
    /** Taglia oltre il piano. */
    sizeOverPlan: 'Non compresa nel piano {{plan}}.',
    sizeNotes: {
      /** Piccola. */
      small: 'Per un progetto alla volta e lavori brevi.',
      /** Media. */
      medium: 'La scelta di partenza: qualche progetto e più agenti.',
      /** Grande. */
      large: 'Per molti agenti al lavoro insieme.',
    },
    /** Etichetta del profilo. */
    profile: 'Profilo degli agenti',
    /** Aiuto del profilo. */
    profileHelp: 'Regole, subagenti e permessi con cui lavorano gli agenti.',
    /** Pulsante che crea il set. */
    submit: 'Crea set',
    /** Esito riuscito. */
    done: 'Set creato.',
    /** Esito fallito. */
    failed: 'Creazione non riuscita. Riprova.',
    /** Rifiuto generico (422). */
    refused: 'Set non creato. Controlla nome, slug e risorse.',
  },
  /** Passo della credenziale. */
  credential: {
    /** Perché serve. */
    intro: 'L’Institore è un agente Claude Code e senza una credenziale non parte.',
    /** Etichetta della scelta. */
    choice: 'Credenziale dell’Institore',
    /** Le tre scelte. */
    options: {
      /** Quella del piano. */
      institor: 'Usa quella di institor',
      /** Spiegazione. */
      institorNote: 'Inclusa nel piano: i token contano per il limite del mese.',
      /** La propria. */
      own: 'Usa la tua',
      /** Spiegazione. */
      ownNote: 'Token dell’abbonamento Claude o chiave API. I token non contano per il limite del piano.',
      /** Rimandare. */
      later: 'Più tardi',
      /** Spiegazione. */
      laterNote: 'L’Institore resta fermo. Aggiungi la credenziale dalle impostazioni del set.',
    },
    /** Il piano non include la credenziale di institor. */
    institorMissing: 'Il tuo piano non include la credenziale di institor: usa la tua oppure aggiungila più tardi.',
  },
  /** Passo del primo progetto. */
  project: {
    /** Che cosa si può fare. */
    intro: 'Scegli da dove parte il lavoro degli agenti. Puoi crearlo anche dopo, dalla sezione Progetti del set.',
    /** Etichetta della scelta del punto di partenza. */
    start: 'Punto di partenza',
    /** Le tre modalità di ADR 0024. */
    startModes: 'Una modalità di lavoro',
    /** Un template pubblicato. */
    startTemplate: 'Un template',
    /** Etichetta del template. */
    template: 'Template',
    /** Aiuto del template. */
    templateHelp: 'Gli agenti copiano i sorgenti del template nel progetto. Le anteprime sono nella pagina Template.',
    /** Template riservato agli abbonati. */
    templateLocked: '{{name}} (con abbonamento)',
    /** Nessun template pubblicato. */
    templatesEmpty: 'Nessun template pubblicato. Scegli una modalità di lavoro.',
    /** Lettura dei template non riuscita. */
    templatesFailed: 'Caricamento dei template non riuscito. Riprova.',
    /** Template non scelto. */
    templateMissing: 'Scegli un template.',
    /** Pulsante che crea il progetto e conclude. */
    submit: 'Crea progetto',
  },
  /** Anteprima dal vivo. */
  preview: {
    /** Titolo. */
    title: 'Anteprima',
    /** Titolo del pannello comprimibile sotto i 1200 px. */
    collapsed: 'Anteprima: {{done}} di {{total}} pronti',
    /** Nome accessibile dell'anello. */
    ring: 'Prontezza del set',
    /** Testo dell'anello. */
    ringValue: '{{done}} di {{total}} pronti',
    /** Set senza nome. */
    noName: 'Set senza nome',
    /** Voce della prontezza fatta, per chi non vede l'icona. */
    checkDone: 'fatto',
    /** Voce della prontezza da fare. */
    checkTodo: 'da fare',
    /** Etichetta del set con tutte le voci fatte. */
    ready: 'Pronto',
    /** Etichetta del set con voci da fare. */
    notReady: 'In preparazione',
    /** Righe. */
    rows: {
      /** Slug. */
      slug: 'Slug',
      /** Taglia e profilo. */
      resources: 'Taglia e profilo',
      /** Credenziale. */
      credential: 'Credenziale',
      /** Progetto. */
      project: 'Primo progetto',
    },
    /** Stato della credenziale. */
    credential: {
      /** Propria, salvata. */
      own: 'Pronta: la tua',
      /** Di institor. */
      institor: 'Pronta: quella di institor',
      /** Rimandata. */
      later: 'Da fare, dalle impostazioni',
      /** Non ancora scelta. */
      pending: 'Da fare',
    },
    /** Stato del progetto. */
    project: {
      /** Non ancora scelto. */
      pending: 'Da fare',
      /** Nome scritto ma non ancora creato. */
      draft: '{{name}}, da creare',
      /** Da un template. */
      template: '{{name}} da {{template}}, da creare',
      /** Creato. */
      created: '{{name}}, creato',
    },
    /** Le quattro voci della prontezza. */
    checks: {
      /** Nome e slug validi. */
      name: 'Nome e slug',
      /** Set creato con le risorse scelte. */
      resources: 'Set creato',
      /** Credenziale pronta. */
      credential: 'Credenziale pronta',
      /** Progetto creato. */
      project: 'Primo progetto',
    },
    /** Che cosa succede alla fine. */
    relayStarts: 'Alla fine l’Institore parte e si apre la conversazione.',
    /** Il relay non parte. */
    relayWaits: 'Senza credenziale l’Institore resta fermo: si apre la conversazione e lo avvii dopo.',
    /** Limiti del piano. */
    limits: {
      /** Titolo. */
      title: 'Limiti del piano',
      /** Piano. */
      plan: 'Piano',
      /** Set posseduti. */
      sets: 'Set',
      /** Set posseduti su massimo. */
      setsValue: '{{owned}} di {{max}}',
      /** Progetti per set. */
      projects: 'Progetti per set',
      /** Token del mese con la credenziale di institor. */
      tokens: 'Token al mese con la credenziale di institor',
      /** Taglia massima del piano. */
      maxSize: 'Taglia massima',
      /** Nessun limite. */
      unlimited: 'Senza limite',
      /** Limiti non letti. */
      unknown: 'Limiti del piano non disponibili.',
    },
  },
  /** Conclusione. */
  finish: {
    /** Relay avviato. */
    relayStarted: 'Institore avviato.',
    /** Avvio del relay non riuscito. */
    relayFailed: 'Avvio dell’Institore non riuscito. Avvialo dalla conversazione.',
  },
};

/** Forma dei testi della procedura. */
export type Setup = typeof setup;
